// frontend/src/components/chat/patient-panel/RoundingBanner.tsx
import { FC, useMemo } from 'react'
import { PatientClinicalData } from '../../../contexts/ChatContext'
import { cn } from '../../../lib/utils'
import { usePatientSignals } from './hooks/usePatientSignals'
import { Signal, SignalSeverity } from './types'

interface RoundingBannerProps {
  data: PatientClinicalData
  isExpanded: boolean
  onToggle: () => void
  lastUpdated?: string
  newSinceLastCount?: number
}

const severityStyles: Record<SignalSeverity, string> = {
  high: 'bg-clinical-coral/15 text-clinical-coral border-clinical-coral/30',
  medium: 'bg-clinical-amber/15 text-clinical-amber border-clinical-amber/30',
  info: 'bg-clinical-teal/15 text-clinical-teal border-clinical-teal/30',
}

const SignalChip: FC<{ signal: Signal }> = ({ signal }) => (
  <span
    className={cn(
      'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border whitespace-nowrap flex-shrink-0',
      severityStyles[signal.severity]
    )}
  >
    {signal.extraction.trend_direction === 'up' && '↑'}
    {signal.extraction.trend_direction === 'down' && '↓'}
    {signal.content}
  </span>
)

export const RoundingBanner: FC<RoundingBannerProps> = ({
  data,
  isExpanded: _isExpanded,
  onToggle: _onToggle,
  lastUpdated,
  newSinceLastCount = 0,
}) => {
  const signals = usePatientSignals(data.extractions)

  // Parse demographics from patient_summary (e.g., "72F with CHF...")
  const demographics = useMemo(() => {
    const match = data.patient_summary.match(/^(\d+[MF])\s+/)
    return match ? match[1] : null
  }, [data.patient_summary])

  return (
    <div className="border-b border-border/50 bg-surface-elevated/50 backdrop-blur-sm">
      {/* Row 1: Demographics + Summary */}
      <div
        className={cn(
          "w-full flex items-center gap-2 py-2 pr-4",
          "pl-4"
        )}
      >
        {demographics && (
          <span className="px-2 py-0.5 rounded bg-muted text-xs font-medium flex-shrink-0">
            {demographics}
          </span>
        )}
        <span className="text-sm text-foreground/80 truncate min-w-0 flex-1">
          {data.patient_summary}
        </span>
        {data.code_status && (
          <span className={cn(
            'px-2 py-0.5 rounded text-xs font-medium flex-shrink-0',
            data.code_status === 'Full code'
              ? 'bg-muted text-muted-foreground'
              : 'bg-clinical-amber/15 text-clinical-amber'
          )}>
            {data.code_status}
          </span>
        )}
      </div>

      {/* Row 2: Signals - horizontal scroll, single row */}
      {signals.length > 0 && (
        <div className={cn(
          "flex items-center gap-2 py-1.5 pr-4 border-t border-border/30",
          "pl-4"
        )}>
          <div className="w-0 flex-1 overflow-x-auto">
            <div className="flex gap-1.5">
              {signals.map(signal => (
                <SignalChip key={signal.id} signal={signal} />
              ))}
            </div>
          </div>
          {(lastUpdated || newSinceLastCount > 0) && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground flex-shrink-0 pl-2 border-l border-border/30">
              {newSinceLastCount > 0 && (
                <span className="px-1.5 py-0.5 rounded bg-clinical-teal/15 text-clinical-teal">
                  +{newSinceLastCount}
                </span>
              )}
              {lastUpdated && <span className="hidden sm:inline">{lastUpdated}</span>}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
